import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useAuth } from "../../auth/AuthContext";
import { useBrand } from "../../brand/BrandContext";
import { useHomeMetrics } from "../../queries/useHomeMetrics";
import {
  useRecentActivity,
  useRiskQueue,
  useScheduleCapacity,
  type RecentActivityItem,
  type ScheduleRoute,
} from "../../queries";
import { GhostMark } from "../GhostMark";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

function formatUsd(value: number | undefined) {
  return `$${Math.round(value || 0).toLocaleString()}`;
}

function formatPct(value: number | undefined) {
  return `${Math.round((value || 0) * 100)}%`;
}

function formatWholePct(value: number | undefined) {
  return `${Math.round(value || 0)}%`;
}

function relativeTime(value: string) {
  const stamp = new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`).getTime();
  if (Number.isNaN(stamp)) return value;
  const diffMs = Date.now() - stamp;
  const minutes = Math.max(0, Math.round(diffMs / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

export function HomeContent() {
  const { user } = useAuth();
  if (user?.role === "sales") return <SalesTodayContent />;
  return <OwnerTodayContent />;
}

function OwnerTodayContent() {
  const { brandName } = useBrand();
  const metrics = useHomeMetrics();
  const riskQueue = useRiskQueue();
  const activity = useRecentActivity(5);
  const locationLabel = brandName ? brandName.replace(/^Sir Grout\s+/i, "") : "Location";
  const riskItems = riskQueue.data?.items || [];
  const activityItems = activity.data?.items || [];
  const openDecisions = riskItems.length;
  const relanceAssisted = metrics.data?.relance_assisted_schedule;

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <Badge variant="slate">Today</Badge>
          <h1 className="mt-3 text-2xl font-semibold tracking-normal text-slate-950 md:text-3xl">
            {locationLabel} today
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Today is the priority queue for the business. Work through the actions below in order, then open a quote to call, reply, or mark it handled.
          </p>
          <TodayHelp />
        </div>
        <Link to="/quotes" className="block md:shrink-0">
          <Button variant="secondary" className="w-full md:w-auto">Search quote or customer</Button>
        </Link>
      </div>

      <section className="grid gap-4 md:grid-cols-4">
        <TodayMetric
          label="Open decisions"
          value={String(openDecisions)}
          detail={openDecisions ? "quotes or alerts need a look" : "nothing urgent right now"}
          tone={openDecisions ? "amber" : "green"}
        />
        <TodayMetric
          label="Follow-up assisted scheduled"
          value={relanceAssisted ? String(relanceAssisted.lifetime.count) : "-"}
          detail={relanceAssisted ? `${relanceAssisted.mtd.count} MTD · ${formatUsd(relanceAssisted.mtd.revenue_usd)} measured` : "loading measured impact"}
          tone="slate"
        />
        <TodayMetric
          label="MTD quote close rate"
          value={metrics.data ? formatPct(metrics.data.conversion_mtd.rate) : "-"}
          detail={metrics.data ? `${metrics.data.conversion_mtd.won_count}/${metrics.data.conversion_mtd.total_count} quotes created this month now scheduled or won` : "loading month-to-date"}
          tone={metrics.data && metrics.data.conversion_mtd.rate < metrics.data.conversion_mtd.target ? "amber" : "green"}
        />
        <TodayMetric
          label="This month"
          value={metrics.data ? String(metrics.data.five_star_reviews_via_agent_mtd) : "-"}
          detail={metrics.data ? "5-star reviews" : "loading review impact"}
          tone="green"
        />
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <CardTitle>Needs attention</CardTitle>
              <span className="text-xs text-slate-500">Call, reply, or review today</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {riskQueue.isLoading ? (
              <EmptyLine label="Loading today's action list..." />
            ) : riskItems.length === 0 ? (
              <div className="rounded-md border border-emerald-100 bg-emerald-50 p-5 text-sm text-emerald-800">
                Nothing urgent right now. Quotes are being tracked normally.
              </div>
            ) : (
              <>
                {riskItems.map((item) => (
                  <ActionRow
                    key={item.id || `risk-${item.quote_id}`}
                    priority={item.priority}
                    title={item.customer_name}
                    ghosted={item.ghosted}
                    reason={`${item.next_action} · ${item.status}`}
                    detail={`${formatUsd(item.value)} · quote #${item.quote_id}`}
                    customerExcerpt={item.excerpt}
                    murrayFocus={item.murray_focus}
                    to="/quotes/$quoteId"
                    params={{ quoteId: item.quote_id }}
                    actionLabel="Open quote"
                    channel={item.source}
                  />
                ))}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>What changed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.isLoading ? (
              <EmptyLine label="Loading recent activity..." />
            ) : activityItems.length === 0 ? (
              <EmptyLine label="No recent activity in the last 7 days." />
            ) : (
              activityItems.map((item, index) => (
                <ActivityLine key={`${item.event_type}-${item.quote_id || index}-${item.occurred_at}`} item={item} />
              ))
            )}
            <Link to="/quotes" className="block pt-1">
              <Button variant="secondary" className="w-full">Open quote control</Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.isLoading ? (
            <EmptyLine label="Loading owner metrics..." />
          ) : metrics.isError || !metrics.data ? (
            <EmptyLine label="Owner metrics unavailable." />
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <OwnerMetric label="Target" value={formatPct(metrics.data.conversion_mtd.target)} />
              <OwnerMetric label="6mo average" value={formatPct(metrics.data.conversion_mtd.historical_rolling_avg_6mo)} />
              <OwnerMetric
                label="Best follow-up"
                value={metrics.data.best_performing_touch?.display_name || "Not enough data"}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function SalesTodayContent() {
  const { brandName } = useBrand();
  const schedule = useScheduleCapacity("tech");
  const riskQueue = useRiskQueue();
  const locationLabel = brandName ? brandName.replace(/^Sir Grout\s+/i, "") : "Location";
  const routes = schedule.data?.routes || [];
  const riskItems = riskQueue.data?.items || [];
  const currentWeekDays = routes.reduce((sum, route) => sum + Number(route.weeks[0]?.days_worked || 0), 0);
  const currentWeekJobs = routes.reduce((sum, route) => sum + Number(route.weeks[0]?.jobs_count || 0), 0);
  const currentTarget = routes.length * Number(schedule.data?.weekly_target.value || 0);
  const currentFill = currentTarget > 0 ? currentWeekDays / currentTarget : 0;

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <Badge variant="slate">Today</Badge>
          <h1 className="mt-3 text-2xl font-semibold tracking-normal text-slate-950 md:text-3xl">
            Today
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            This is the priority queue for the business. Handle the actions below in order, with technician capacity shown as context.
          </p>
          <TodayHelp />
        </div>
        <Link to="/quotes" className="block md:shrink-0">
          <Button variant="secondary" className="w-full md:w-auto">Search quote or customer</Button>
        </Link>
      </div>

      <section className="grid gap-4 md:grid-cols-3">
        <TodayMetric
          label="Crew capacity"
          value={schedule.isLoading ? "-" : formatPct(currentFill)}
          detail={schedule.data ? `${currentWeekDays}/${currentTarget || 0} crew days this week · ${currentWeekJobs} jobs` : "loading schedule"}
          tone={currentFill >= 1 ? "green" : currentFill >= 0.7 ? "amber" : "slate"}
        />
        <TodayMetric
          label="Today priorities"
          value={riskQueue.isLoading ? "-" : String(riskItems.length)}
          detail={riskItems.length ? "actions to handle in order" : "nothing urgent right now"}
          tone={riskItems.length ? "amber" : "green"}
        />
        <TodayMetric
          label="Location"
          value={locationLabel}
          detail="Quote360, SMS history, and crew capacity"
          tone="slate"
        />
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>Today priorities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {riskQueue.isLoading ? (
              <EmptyLine label="Loading priorities..." />
            ) : riskQueue.isError ? (
              <EmptyLine label="Priorities could not load." />
            ) : riskItems.length === 0 ? (
              <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-800">
                Nothing urgent right now. Quotes are being tracked normally.
              </div>
            ) : (
              riskItems.map((item) => (
                <ActionRow
                  key={item.id || `risk-${item.quote_id}`}
                  priority={item.priority}
                  title={item.customer_name}
                  ghosted={item.ghosted}
                  reason={`${item.next_action} · ${item.status}`}
                  detail={`${formatUsd(item.value)} · quote #${item.quote_id}`}
                  customerExcerpt={item.excerpt}
                  murrayFocus={item.murray_focus}
                  to="/quotes/$quoteId"
                  params={{ quoteId: item.quote_id }}
                  actionLabel="Open quote"
                  channel={item.source}
                />
              ))
            )}
            <Link to="/quotes" className="block pt-1">
              <Button variant="secondary" className="w-full">Open quote control</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Technician capacity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {schedule.isLoading ? (
              <EmptyLine label="Loading technician capacity..." />
            ) : schedule.isError ? (
              <EmptyLine label="Schedule capacity could not load." />
            ) : routes.length === 0 ? (
              <EmptyLine label="No technician routes configured yet." />
            ) : (
              routes.map((route) => <SalesRouteRow key={route.route_id} route={route} />)
            )}
            <Link to="/schedule" className="block pt-1">
              <Button variant="secondary" className="w-full">Open full capacity</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function SalesRouteRow({ route }: { route: ScheduleRoute }) {
  const week = route.weeks[0];
  const fill = week ? formatWholePct(week.fill_pct) : "-";
  const tone = week?.bucket === "GREEN" || week?.bucket === "DARK_GREEN" ? "green" : week?.bucket === "AMBER" ? "amber" : "slate";
  const daysWorked = Number(week?.days_worked || 0);
  const jobs = Number(week?.jobs_count || 0);
  return (
    <div className="grid gap-3 rounded-md border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_auto] md:items-center">
      <div className="min-w-0">
        <div className="font-medium text-slate-950">{route.route_label}</div>
        <div className="mt-1 text-sm text-slate-600">{route.collaborator_name}</div>
        <div className="mt-1 text-xs text-slate-500">{week?.display_label_long || "Current week"} · {daysWorked}d · {jobs} jobs</div>
      </div>
      <Badge variant={tone}>{fill}</Badge>
    </div>
  );
}

function TodayHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative mt-3 inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="inline-flex h-8 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-950"
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold">?</span>
        How to use Today
      </button>
      {open ? (
        <div className="absolute left-0 top-10 z-30 w-[min(320px,calc(100vw-32px))] rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-lg">
          <div className="font-semibold text-slate-950">Simple routine</div>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            <li>Start with the first customer.</li>
            <li>Open the quote and read the latest email or SMS.</li>
            <li>Call, reply, or mark it handled.</li>
            <li>Use Closed only when no more follow-up is needed.</li>
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function TodayMetric({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: "green" | "amber" | "slate";
}) {
  const valueClass = tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-950";
  return (
    <Card className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`mt-2 text-3xl font-semibold tracking-normal ${valueClass}`}>{value}</div>
      <p className="mt-2 text-sm text-slate-500">{detail}</p>
    </Card>
  );
}

function ActionRow({
  priority,
  title,
  reason,
  detail,
  customerExcerpt,
  murrayFocus,
  to,
  params,
  actionLabel,
  channel,
  ghosted,
}: {
  priority: "Critical" | "High" | "Watch";
  title: string;
  reason: string;
  detail: string;
  customerExcerpt?: string | null;
  murrayFocus?: {
    id: string;
    title: string;
    action: string | null;
    target_metric: string | null;
  } | null;
  to: "/quotes/$quoteId";
  params: { quoteId: string };
  actionLabel: string;
  channel?: "email" | "sms" | "call_task" | "reminder";
  ghosted?: boolean;
}) {
  const variant = priority === "Critical" ? "red" : priority === "High" ? "amber" : "slate";
  const channelLabel = channel === "sms" ? "SMS" : channel === "call_task" ? "Call" : channel === "reminder" ? "Reminder" : null;
  return (
    <div className="grid gap-3 rounded-md border border-slate-100 bg-white p-3 md:grid-cols-[120px_1fr_auto] md:items-center">
      <div className="flex gap-2 md:block md:space-y-2">
        <Badge variant={variant}>{priority}</Badge>
        {channelLabel ? <Badge variant="slate">{channelLabel}</Badge> : null}
        {murrayFocus ? <Badge variant="green">Murray focus</Badge> : null}
      </div>
      <div className="min-w-0">
        <div className="font-medium text-slate-950">{title}<GhostMark ghosted={ghosted} /></div>
        <div className="mt-1 text-sm text-slate-600">{reason}</div>
        {murrayFocus?.action ? (
          <div className="mt-1 line-clamp-2 text-xs font-medium text-emerald-800">
            Suggested next action: {murrayFocus.action}
          </div>
        ) : null}
        <div className="mt-1 text-xs text-slate-500">{detail}</div>
        {customerExcerpt ? (
          <div className="mt-1 line-clamp-2 text-xs italic text-slate-500">"{customerExcerpt}"</div>
        ) : null}
      </div>
      <Link to={to} params={params} className="block">
        <Button className="w-full md:w-auto" variant={priority === "Critical" ? "default" : "secondary"}>{actionLabel}</Button>
      </Link>
    </div>
  );
}

function ActivityLine({ item }: { item: RecentActivityItem }) {
  const isCustomerMessage = item.event_type === "reply_received";
  const content = (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="font-medium text-slate-950">{item.title}</div>
        <div className="shrink-0 text-xs text-slate-400">{relativeTime(item.occurred_at)}</div>
      </div>
      <div className="mt-1 truncate text-xs text-slate-500">
        {item.customer_name ? <>{item.customer_name}<GhostMark ghosted={item.ghosted} />{" · "}</> : ""}
        <span className={isCustomerMessage ? "italic" : undefined}>{item.detail || "No detail"}</span>
      </div>
    </div>
  );

  if (!item.quote_id) return content;
  return (
    <Link to="/quotes/$quoteId" params={{ quoteId: item.quote_id }} className="block hover:opacity-90">
      {content}
    </Link>
  );
}

function OwnerMetric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-2 break-words text-lg font-semibold leading-tight text-slate-950">{value}</div>
      {detail ? <div className="mt-1 text-xs text-slate-500">{detail}</div> : null}
    </div>
  );
}

function EmptyLine({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-4 text-center text-sm text-slate-500">
      {label}
    </div>
  );
}
